import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  EntityCreateScreen,
  EntityDetailScreen,
  EntityEditScreen,
  EntityListScreen,
  SidebarNav,
  type SidebarNavItem,
  type UiEntitySpec,
  type UiEntityListConfig,
  type UiListColumnSpec,
  SelectInput,
  useCreateEntity,
  useEntityList,
} from "@pericles/framework-frontend";
import { appUiSpec } from "@pericles/app-project-requirements-config";

import { bootstrapProjectBaseline } from "../api/projectBootstrapClient";
import { useGenerateRequirementWithAI } from "../api/requirementsClient";
import {
  fetchFoundryV2NavigationSnapshot,
  type FoundryV2NavigationSnapshotRecord,
} from "../api/foundryV2Client";
import { ACTIVE_CUSTOMER_LABEL, ALL_CUSTOMERS_LABEL, CUSTOMER_LABEL } from "../config/brand";
import {
  FEATURE_JARVIS_THEME,
  FEATURE_JARVIS_THEME_NAME,
  FEATURE_JARVIS_V2,
  FEATURE_NAV_V01,
  useFeatureFlag,
} from "../config/featureFlags";
import { buildSidebarNavFromConfig } from "../config/navigation";
import { AuthProvider, useAuth } from "../hooks/auth";
import { InternalCapabilitiesProvider } from "../hooks/internalCapabilities";
import { I18nProvider, useI18n } from "../i18n";
import {
  applyFoundryV2NavigationSnapshotToSidebar,
  isFoundryV2RolloutAwarePath,
  resolveFoundryV2ProjectId,
  resolveFoundryV2RouteRedirect,
  resolveFoundryV2SidebarHref,
} from "../domain/foundryV2Navigation";

import {
  RequirementsAwaitingGenerationListScreen,
  RequirementsListScreen,
  RequirementsToReviewListScreen,
  type RequirementsView,
} from "./RequirementsListScreen";
import { CustomersListScreen } from "./CustomersListScreen";
import { ProjectsListScreen } from "./ProjectsListScreen";
import { RequirementEditPanel } from "./RequirementEditPanel";
import { RequirementDetailScreen } from "./RequirementDetailScreen";
import { ProjectChecklistPanel } from "./ProjectChecklistPanel";
import { ProjectWorkplanPanel } from "./ProjectWorkplanPanel";
import { ProjectSpecificationPanel } from "./ProjectSpecificationPanel";
import { ProjectConversationPanel } from "./ProjectConversationPanel";
import { GovernanceScreen } from "./GovernanceScreen";
import { ProjectDetailScreen } from "./ProjectDetailScreen";
import { ProjectBriefPanel } from "./ProjectBriefPanel";
import { InputDataScreen } from "./InputDataScreen";
import { CustomerFormPanel } from "./CustomerFormPanel";
import { ProjectFormPanel } from "./ProjectFormPanel";
import { DiscoveryReadinessPanel } from "./DiscoveryReadinessPanel";
import { AiRoadmapScreen } from "./AiRoadmapScreen";
import { JarvisThemeLink } from "./JarvisThemeLink";
import { TenantSelectScreen } from "./TenantSelectScreen";
import { TenantSwitcher } from "./TenantSwitcher";
import { LoginScreen } from "./LoginScreen";
import { PortfolioRunsScreen } from "./PortfolioRunsScreen";
import { PortfolioScreen } from "./PortfolioScreen";
import { HomeDashboard } from "./HomeDashboard";
import { JarvisLandingScreen } from "./JarvisLandingScreen";
import { JarvisV2WorkspaceScreen } from "./JarvisV2WorkspaceScreen";
import { RuntimeCockpitScreen } from "./RuntimeCockpitPanel";
import { LongWallsMilestonesScreen } from "./LongWallsMilestonesScreen";
import { FoundryV2Screen } from "./FoundryV2Screen";
import { GovernanceV2Screen } from "./GovernanceV2Screen";
import { ForumScreen } from "./ForumScreen";
import { AuthenticatedPericlesProvider } from "./AuthenticatedPericlesProvider";

type DetailTabKey =
  | "overview"
  | "requirements"
  | "specification"
  | "coverage"
  | "workplan"
  | "conversation"
  | "brief";

interface CustomerListItem {
  id: string | number;
  name?: string;
}

interface RequirementDraftFormState {
  title: string;
  description: string;
  sourceType: "manual" | "pdf_summary" | "meeting_ai" | "other_ai";
  generationTarget: "backend_module" | "frontend_module" | "full_stack_module";
  sourceMeetingId: string;
}

const DEFAULT_REQUIREMENT_DRAFT: RequirementDraftFormState = {
  title: "",
  description: "",
  sourceType: "manual",
  generationTarget: "full_stack_module",
  sourceMeetingId: "",
};

interface ProjectDetailTabsProps {
  entity: UiEntitySpec;
  id: string | number;
  title?: string;
  onBack?: () => void;
  onDeleted?: () => void;
  specRefreshKey: number;
  onSpecUpdated: () => void;
}

function ProjectDetailTabs({
  entity,
  id,
  title,
  onBack,
  onDeleted,
  specRefreshKey,
      onSpecUpdated,
    }: ProjectDetailTabsProps): JSX.Element {
  const { activeTenantId } = useAuth();
  const projectId = id ? String(id) : "";
  const [activeTab, setActiveTab] = useState<DetailTabKey>("overview");
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [selectedRequirementId, setSelectedRequirementId] = useState<string | number | null>(null);
  const [requirementsRefreshKey, setRequirementsRefreshKey] = useState<number>(0);
  const [isCreateRequirementModalOpen, setIsCreateRequirementModalOpen] = useState(false);
  const [createRequirementDraft, setCreateRequirementDraft] =
    useState<RequirementDraftFormState>(DEFAULT_REQUIREMENT_DRAFT);
  const [createRequirementError, setCreateRequirementError] = useState<string | null>(null);
  const createRequirement = useCreateEntity<Record<string, unknown>, Record<string, unknown>>({
    entity: "Requirement",
  });

  const requirementEntity = useMemo(
    () => appUiSpec.entities.find((item) => item.entityName === "Requirement"),
    [],
  );

  useEffect(() => {
    setActiveTab("overview");
    setSelectedRequirementId(null);
    setIsCreateRequirementModalOpen(false);
    setCreateRequirementDraft(DEFAULT_REQUIREMENT_DRAFT);
    setCreateRequirementError(null);
  }, [id]);

  const openCreateRequirementModal = useCallback(() => {
    setCreateRequirementError(null);
    setCreateRequirementDraft(DEFAULT_REQUIREMENT_DRAFT);
    setIsCreateRequirementModalOpen(true);
  }, []);

  const closeCreateRequirementModal = useCallback(() => {
    setCreateRequirementError(null);
    setCreateRequirementDraft(DEFAULT_REQUIREMENT_DRAFT);
    setIsCreateRequirementModalOpen(false);
  }, []);

  const handleRequirementDraftChange = useCallback(
    (field: keyof RequirementDraftFormState, value: string) => {
      setCreateRequirementError(null);
      setCreateRequirementDraft((previous) => ({
        ...previous,
        [field]: value,
      }));
    },
    [],
  );

  const handleCreateRequirement = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!projectId) {
        setCreateRequirementError("Project context is missing.");
        return;
      }

      const titleValue = createRequirementDraft.title.trim();
      const descriptionValue = createRequirementDraft.description.trim();

      if (!titleValue || !descriptionValue) {
        setCreateRequirementError("Title and Description are required.");
        return;
      }

      const payload: Record<string, unknown> = {
        projectId,
        project: projectId,
        title: titleValue,
        description: descriptionValue,
        sourceType: createRequirementDraft.sourceType,
        generationTarget: createRequirementDraft.generationTarget,
      };

      const sourceMeetingId = createRequirementDraft.sourceMeetingId.trim();
      if (sourceMeetingId.length > 0) {
        payload.sourceMeetingId = sourceMeetingId;
      }

      if (activeTenantId) {
        payload.tenantId = activeTenantId;
      }

      const created = await createRequirement.create(payload);
      if (created === undefined) {
        setCreateRequirementError(
          createRequirement.error instanceof Error
            ? createRequirement.error.message
            : "Failed to create requirement.",
        );
        return;
      }

      const createdId =
        typeof created === "object" && created !== null && "id" in created
          ? (created as { id?: string | number }).id ?? null
          : null;

      setRequirementsRefreshKey((previous) => previous + 1);
      setSelectedRequirementId(createdId);
      closeCreateRequirementModal();
    },
    [
      activeTenantId,
      closeCreateRequirementModal,
      createRequirement,
      createRequirementDraft,
      projectId,
    ],
  );

  async function handleBootstrapBaseline() {
    if (!projectId) return;

    const confirmed = window.confirm(
      "This will create baseline requirements and checklist items for this project using Pericles templates. Continue?",
    );
    if (!confirmed) return;

    try {
      setIsBootstrapping(true);
      const result = await bootstrapProjectBaseline(projectId, {
        includeRequirements: true,
        includeChecklist: true,
      });

      alert(
        `Baseline created for project ${result.projectId}:\n` +
          `- Requirements: ${result.createdRequirements} created, ${result.skippedRequirements} skipped\n` +
          `- Checklist items: ${result.createdChecklistItems} created, ${result.skippedChecklistItems} skipped`,
      );
    } catch (error: any) {
      console.error(error);
      alert(error?.message ?? "Failed to bootstrap project baseline.");
    } finally {
      setIsBootstrapping(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`button ${activeTab === "overview" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          className={`button ${activeTab === "requirements" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("requirements")}
        >
          Requirements
        </button>
        <button
          type="button"
          className={`button ${activeTab === "specification" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("specification")}
        >
          Specs
        </button>
        <button
          type="button"
          className={`button ${activeTab === "workplan" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("workplan")}
        >
          Workplan
        </button>
        <button
          type="button"
          className={`button ${activeTab === "coverage" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("coverage")}
        >
          Coverage
        </button>
        <button
          type="button"
          className={`button ${activeTab === "conversation" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("conversation")}
        >
          Conversation
        </button>
        <button
          type="button"
          className={`button ${activeTab === "brief" ? "button-primary" : "button-secondary"}`}
          onClick={() => setActiveTab("brief")}
          >
            Project Brief
          </button>
        </div>

        {activeTab === "overview" ? (
          <div className="flex flex-col gap-3">
            <EntityDetailScreen
              key={`project-detail-${id}`}
              entity={entity}
            id={id}
            titleOverride={title}
            onBack={onBack}
            onDeleted={onDeleted}
          />

          <div className="rounded border p-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-base font-semibold text-slate-900">Baseline</div>
                <button
                  type="button"
                  className="button button-secondary button-sm"
                  onClick={handleBootstrapBaseline}
                  disabled={isBootstrapping}
                >
                  {isBootstrapping ? "Bootstrapping..." : "Bootstrap baseline"}
                </button>
              </div>
              <p className="text-sm text-slate-700">
                Configurer les requirements de base et la checklist standard pour ce projet.
              </p>
            </div>
          </div>
        </div>
      ) : activeTab === "requirements" ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm text-slate-700">
              Create requirements in a focused modal. Project and tenant context are injected automatically.
            </p>
            <button
              type="button"
              className="button button-primary button-sm"
              onClick={openCreateRequirementModal}
              disabled={!projectId}
            >
              New requirement
            </button>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <RequirementsListScreen
              key={`${projectId}-${requirementsRefreshKey}`}
              entityName="Requirement"
              title="Project requirements"
              filters={{ projectId } as any}
              onRowClick={(row: any) => setSelectedRequirementId((row as any)?.id ?? null)}
              filterItems={(row: any) => {
                const project = (row as any)?.project;
                const directId = (row as any)?.projectId ?? (project && typeof project === "object" ? project.id : null);
                return directId ? String(directId) === projectId : true;
              }}
            />

            <div className="flex flex-col gap-4">
              {requirementEntity && selectedRequirementId ? (
                <RequirementDetailScreen
                  key={`project-req-detail-${selectedRequirementId}-${requirementsRefreshKey}`}
                  entity={requirementEntity}
                  id={selectedRequirementId}
                  onDeleted={() => {
                    setSelectedRequirementId(null);
                    setRequirementsRefreshKey((previous) => previous + 1);
                  }}
                />
              ) : (
                <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
                  Select a requirement from the list to view details.
                </div>
              )}
            </div>
          </div>

          {isCreateRequirementModalOpen ? (
            <div
              className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4"
              onClick={(event) => {
                if (event.target === event.currentTarget && !createRequirement.isSubmitting) {
                  closeCreateRequirementModal();
                }
              }}
            >
              <div className="w-full max-w-3xl rounded-xl border border-slate-200 bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-lg font-semibold text-slate-900">Create Requirement</h3>
                    <p className="text-sm text-slate-600">
                      Project: {projectId} {activeTenantId ? `| Tenant: ${activeTenantId}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={closeCreateRequirementModal}
                    disabled={createRequirement.isSubmitting}
                  >
                    Close
                  </button>
                </div>

                <form className="flex flex-col gap-4 px-5 py-5" onSubmit={handleCreateRequirement}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Title *
                      <input
                        type="text"
                        value={createRequirementDraft.title}
                        onChange={(event) => handleRequirementDraftChange("title", event.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Requirement title"
                        required
                      />
                    </label>

                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Source type *
                      <select
                        value={createRequirementDraft.sourceType}
                        onChange={(event) => handleRequirementDraftChange("sourceType", event.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        required
                      >
                        <option value="manual">Manual</option>
                        <option value="pdf_summary">PDF summary</option>
                        <option value="meeting_ai">Meeting AI</option>
                        <option value="other_ai">Other AI</option>
                      </select>
                    </label>
                  </div>

                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    Description *
                    <textarea
                      value={createRequirementDraft.description}
                      onChange={(event) => handleRequirementDraftChange("description", event.target.value)}
                      className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Describe the business and technical expectation"
                      required
                    />
                  </label>

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Generation target *
                      <select
                        value={createRequirementDraft.generationTarget}
                        onChange={(event) => handleRequirementDraftChange("generationTarget", event.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        required
                      >
                        <option value="full_stack_module">Full-stack module</option>
                        <option value="backend_module">Backend module</option>
                        <option value="frontend_module">Frontend module</option>
                      </select>
                    </label>

                    <label className="flex flex-col gap-1 text-sm text-slate-700">
                      Source meeting ID (optional)
                      <input
                        type="text"
                        value={createRequirementDraft.sourceMeetingId}
                        onChange={(event) => handleRequirementDraftChange("sourceMeetingId", event.target.value)}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="meeting_xxx"
                      />
                    </label>
                  </div>

                  {createRequirementError ? (
                    <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {createRequirementError}
                    </p>
                  ) : null}

                  <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={closeCreateRequirementModal}
                      disabled={createRequirement.isSubmitting}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="button button-primary" disabled={createRequirement.isSubmitting}>
                      {createRequirement.isSubmitting ? "Creating..." : "Create requirement"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : null}
        </div>
      ) : activeTab === "specification" ? (
        <ProjectSpecificationPanel projectId={String(id)} refreshKey={specRefreshKey} />
      ) : activeTab === "conversation" ? (
        <ProjectConversationPanel
          projectId={String(id)}
          onSpecUpdated={onSpecUpdated}
        />
      ) : activeTab === "brief" ? (
        <ProjectBriefPanel projectId={projectId} />
      ) : activeTab === "workplan" ? (
        <ProjectWorkplanPanel projectId={String(id)} />
      ) : (
        <ProjectChecklistPanel projectId={String(id)} />
      )}
    </div>
  );
}

type ProjectRequirementsAppSpec = typeof appUiSpec;
type UiScreenSpec = NonNullable<ProjectRequirementsAppSpec["screens"]>[number];

const DEFAULT_BASE_URL = "/api";
const DEFAULT_HOME_ROUTE = "/longwalls";
const NAV_V01_REMOVED_IDS = new Set([
  "requirements_to_review_list",
  "requirements_to_generate_list",
  "spec_sections_list",
  "work_items_list",
  "project_meetings_list",
]);

function removeNavItems(items: SidebarNavItem[], idsToRemove: Set<string>): SidebarNavItem[] {
  return items
    .map((item) => {
      const shouldRemove = (item.id && idsToRemove.has(item.id)) || (item.href && idsToRemove.has(item.href));
      if (shouldRemove) return null;

      const children = item.children ? removeNavItems(item.children, idsToRemove) : undefined;
      if (children && children.length === 0 && !item.href) {
        return null;
      }

      return { ...item, ...(children ? { children } : {}) };
    })
    .filter(Boolean) as SidebarNavItem[];
}

function rolloutBannerClass(tone: FoundryV2NavigationSnapshotRecord["shellBanner"]["tone"]): string {
  if (tone === "emerald") return "border-emerald-200 bg-emerald-50 text-emerald-900";
  if (tone === "amber") return "border-amber-200 bg-amber-50 text-amber-900";
  if (tone === "orange") return "border-orange-200 bg-orange-50 text-orange-900";
  return "border-slate-200 bg-slate-50 text-slate-900";
}

function parseRequirementsViewParam(value: string | null | undefined): RequirementsView | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "to_review" || normalized === "review" || normalized === "review_queue") return "to_review";
  if (normalized === "to_generate" || normalized === "generate" || normalized === "generation") return "to_generate";
  if (normalized === "all") return "all";
  return null;
}

function mergeListConfig(
  base: UiEntityListConfig,
  screenOverride?: {
    columns?: UiListColumnSpec[];
    defaultFilters?: unknown;
    title?: string;
  },
): UiEntityListConfig {
  if (!screenOverride) {
    return base;
  }

  return {
    ...base,
    columns: screenOverride.columns ?? base.columns,
    ...(screenOverride.defaultFilters != null && {
      // @ts-ignore champ d’extension non connu par le type d’origine
      defaultFilters: screenOverride.defaultFilters,
    }),
  } as UiEntityListConfig;
}

function mergeDetailConfig(
  baseEntity: UiEntitySpec,
  screen: { sections?: UiEntitySpec["detail"] extends { sections?: infer S } ? S : unknown } | any,
): UiEntitySpec {
  return {
    ...baseEntity,
    detail: {
      ...baseEntity.detail,
      sections:
        screen && typeof screen === "object" && "sections" in screen && (screen as any).sections != null
          ? (screen as any).sections
          : baseEntity.detail?.sections,
    },
  };
}

function AppShellContent(): JSX.Element {
  const appSpec = appUiSpec;
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, activeTenantId } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const headerRef = useRef<HTMLElement | null>(null);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const foundryV2ProjectId = useMemo(() => {
    if (typeof window === "undefined") return "";
    return resolveFoundryV2ProjectId({
      pathname: location.pathname,
      search: location.search,
      handoffStoragePayload: window.localStorage.getItem("longwalls.v2.handoffPayload"),
    });
  }, [location.pathname, location.search]);
  const [foundryV2Navigation, setFoundryV2Navigation] = useState<FoundryV2NavigationSnapshotRecord | null>(null);
  const [foundryV2NavigationError, setFoundryV2NavigationError] = useState<string | null>(null);
  const sidebarItems = useMemo<SidebarNavItem[]>(() => {
    let items = buildSidebarNavFromConfig(undefined, t);
    if (FEATURE_NAV_V01) {
      items = removeNavItems(items, NAV_V01_REMOVED_IDS);
    }
    if (!FEATURE_JARVIS_V2) {
      items = items.filter((item) => item.id !== "jarvis_v2");
    }
    items = applyFoundryV2NavigationSnapshotToSidebar(items, foundryV2Navigation);
    return items;
  }, [FEATURE_JARVIS_V2, FEATURE_NAV_V01, foundryV2Navigation, t]);
  const screens = (appSpec.screens ?? []) as UiScreenSpec[];
  const [activeHref, setActiveHref] = useState<string>(sidebarItems[0]?.href ?? DEFAULT_HOME_ROUTE);
  const [selectedIds, setSelectedIds] = useState<Record<string, string | number | null>>({});
  const [activeCustomerId, setActiveCustomerId] = useState<string>("all");
  const [listRefreshKey, setListRefreshKey] = useState<number>(0);
  const [formResetKey, setFormResetKey] = useState<number>(0);
  const [specRefreshKey, setSpecRefreshKey] = useState<number>(0);
  const [generationActiveId, setGenerationActiveId] = useState<string | number | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem("studio.theme");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });
  const [jarvisThemeEnabled] = useFeatureFlag(FEATURE_JARVIS_THEME_NAME, FEATURE_JARVIS_THEME);
  const isProjectDetailRoute = location.pathname.startsWith("/projects/");
  const requirementsViewFromParams = useMemo(
    () => parseRequirementsViewParam(searchParams.get("filter")),
    [searchParams],
  );
  const {
    generate: triggerRequirementGeneration,
    isLoading: isGeneratingRequirement,
    error: generationError,
  } = useGenerateRequirementWithAI();

  useEffect(() => {
    if (!FEATURE_JARVIS_V2 || !foundryV2ProjectId || !isFoundryV2RolloutAwarePath(location.pathname)) {
      setFoundryV2Navigation(null);
      setFoundryV2NavigationError(null);
      return;
    }

    let cancelled = false;
    setFoundryV2NavigationError(null);

    void fetchFoundryV2NavigationSnapshot(foundryV2ProjectId)
      .then((snapshot) => {
        if (cancelled) return;
        setFoundryV2Navigation(snapshot);
      })
      .catch((cause) => {
        if (cancelled) return;
        setFoundryV2Navigation(null);
        setFoundryV2NavigationError(
          cause instanceof Error ? cause.message : "Failed to load the tenant cutover posture.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [FEATURE_JARVIS_V2, foundryV2ProjectId, location.pathname]);

  useEffect(() => {
    if (!FEATURE_JARVIS_V2 || !foundryV2Navigation) {
      return;
    }

    const redirectHref = resolveFoundryV2RouteRedirect(location.pathname, foundryV2Navigation);
    if (!redirectHref) {
      return;
    }

    const currentHref = `${location.pathname}${location.search}`;
    if (currentHref === redirectHref) {
      return;
    }

    navigate(redirectHref, { replace: true });
  }, [FEATURE_JARVIS_V2, foundryV2Navigation, location.pathname, location.search, navigate]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const updateHeaderHeight = () => {
      const height = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--app-header-height", `${height}px`);
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(header);
    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);
  const handleLogout = useCallback(async () => {
    await fetch("/auth/logout", { method: "POST" });
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate, setUser]);

  const { data: customerOptionsData, isLoading: isLoadingCustomers } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement | null>(null);

  const navByHref = useMemo(() => {
    const map = new Map<string, SidebarNavItem>();
    const register = (item: SidebarNavItem) => {
      map.set(item.href, item);
      item.children?.forEach(register);
    };
    sidebarItems.forEach(register);
    return map;
  }, [sidebarItems]);

  useEffect(() => {
    if (!FEATURE_NAV_V01) return;
    if (location.pathname === "/requirements_to_review_list") {
      navigate("/requirements_list?filter=to_review", { replace: true });
    } else if (location.pathname === "/requirements_to_generate_list") {
      navigate("/requirements_list?filter=to_generate", { replace: true });
    }
  }, [location.pathname, navigate]);

  const resolveModuleHref = useCallback((pathname: string, search: string): string | null => {
    const searchParams = new URLSearchParams(search);
    const governanceTab = searchParams.get("tab");
    if (pathname === "/longwalls" || pathname === "/home" || pathname === "/milestones") return "/longwalls";
    if (pathname === "/accounts" || pathname === "/customers_list") return "/accounts";
    if (pathname === "/atlas" || pathname.startsWith("/ai_roadmap")) return "/atlas";
    if (pathname === "/forum") return "/forum";
    if (
      pathname === "/foundry" ||
      pathname === "/projects_list" ||
      pathname.startsWith("/projects/") ||
      pathname === "/requirements_list" ||
      pathname === "/requirements_to_review_list" ||
      pathname === "/requirements_to_generate_list" ||
      pathname === "/spec_sections_list" ||
      pathname === "/work_items_list" ||
      pathname === "/project_meetings_list" ||
      pathname === "/checklist_items_list" ||
      pathname === "/input_data"
    )
      return "/foundry";
    if (pathname === "/jarvis" || pathname === "/runtime-cockpit" || pathname === "/portfolio_runs" || pathname === "/portfolio") return "/jarvis";
    if (pathname === "/foundry-v2") return "/foundry";
    if (pathname === "/jarvis-v2") return "/jarvis";
    if (pathname === "/governance-v2") return "/governance";
    if (
      pathname === "/governance" ||
      pathname === "/workspace_settings" ||
      pathname.startsWith("/settings/")
    ) {
      return "/governance";
    }
    return null;
  }, []);

  useEffect(() => {
    const moduleHref = resolveModuleHref(location.pathname, location.search);
    if (moduleHref) {
      setActiveHref(moduleHref);
      return;
    }

    if (navByHref.has(location.pathname)) {
      setActiveHref(location.pathname);
      return;
    }

    if (!location.pathname || location.pathname === "/") {
      setActiveHref(sidebarItems[0]?.href ?? DEFAULT_HOME_ROUTE);
    }
  }, [location.pathname, navByHref, resolveModuleHref, sidebarItems]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", isDarkMode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("studio.theme", isDarkMode ? "dark" : "light");
    }
  }, [isDarkMode]);

  useEffect(() => {
    setActiveCustomerId("all");
    setSelectedIds({});
    setListRefreshKey((previous) => previous + 1);
    setFormResetKey((previous) => previous + 1);
    setSpecRefreshKey((previous) => previous + 1);
  }, [activeTenantId]);

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));

    if (activeCustomerId !== "all" && !options.some((option) => option.value === activeCustomerId)) {
      options.push({ value: activeCustomerId, label: `${CUSTOMER_LABEL} ${activeCustomerId}` });
    }

    return [{ value: "all", label: ALL_CUSTOMERS_LABEL }, ...options];
  }, [activeCustomerId, customerOptionsData]);

  const handleActiveCustomerChange = useCallback((nextId: string) => {
    setActiveCustomerId(nextId || "all");
    setSelectedIds({});
    setListRefreshKey((previous) => previous + 1);
  }, []);

  const handleCustomerSelect = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      handleActiveCustomerChange(event.target.value || "all");
    },
    [handleActiveCustomerChange],
  );

  const screensByRoute = useMemo(() => {
    const map = new Map<string, any>();
    screens.forEach((screen: any) => {
      if (!screen?.route) return;
      map.set(screen.route as string, screen);
    });
    return map;
  }, [screens]);

  const screensById = useMemo(() => {
    const map = new Map<string, any>();
    screens.forEach((screen: any) => {
      if (!screen?.id) return;
      map.set(screen.id as string, screen);
    });
    return map;
  }, [screens]);

  const handleNavigate = useCallback(
    (href: string) => {
      setActiveHref(href);
      navigate(resolveFoundryV2SidebarHref(href, foundryV2Navigation));
      setSelectedIds({});
      setIsMobileNavOpen(false);
    },
    [foundryV2Navigation, navigate],
  );

  useEffect(() => {
    if (!isMobileNavOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (isMobileNavOpen) {
      const previousBodyOverflow = document.body.style.overflow;
      const activeSidebarRef = sidebarRef.current;
      document.body.style.overflow = "hidden";

      const firstNavItem = activeSidebarRef?.querySelector<HTMLElement>(".sidebar-nav__link");
      firstNavItem?.focus();

      return () => {
        document.body.style.overflow = previousBodyOverflow;
        if (!activeSidebarRef || !activeSidebarRef.contains(document.activeElement)) {
          menuButtonRef.current?.focus();
        }
      };
    }
  }, [isMobileNavOpen]);

  const handleRequirementsViewChange = useCallback(
    (nextView: RequirementsView) => {
      if (!FEATURE_NAV_V01) return;
      const params = new URLSearchParams(location.search);
      if (nextView === "all") {
        params.delete("filter");
      } else {
        params.set("filter", nextView);
      }
      const query = params.toString();
      navigate(`/requirements_list${query ? `?${query}` : ""}`, { replace: true });
    },
    [location.search, navigate],
  );

  const activeNavItem = navByHref.get(activeHref) ?? sidebarItems[0];
  const shouldShowFoundryV2Banner =
    FEATURE_JARVIS_V2 &&
    Boolean(foundryV2Navigation) &&
    Boolean(foundryV2ProjectId) &&
    isFoundryV2RolloutAwarePath(location.pathname);
  const activeScreen = useMemo(() => {
    const routeScreen = screensByRoute.get(location.pathname);
    if (routeScreen) return routeScreen;
    if (!activeNavItem) return undefined;
    if (activeNavItem.screenId) {
      return screensById.get(activeNavItem.screenId);
    }
    return screensByRoute.get(activeNavItem.href);
  }, [activeNavItem, location.pathname, screensById, screensByRoute]);

  const baseEntity = useMemo(() => {
    if (!activeScreen) return undefined;
    return appSpec.entities.find((entity: UiEntitySpec) => entity.entityName === activeScreen.entity);
  }, [activeScreen, appSpec.entities]);

  const isRequirementEntity = baseEntity?.entityName === "Requirement";
  const isProjectEntity = baseEntity?.entityName === "Project";

  const createInitialValues = useMemo(() => {
    if (isProjectEntity && activeCustomerId !== "all") {
      return { customerId: activeCustomerId } as Record<string, unknown>;
    }

    return undefined;
  }, [activeCustomerId, isProjectEntity]);

  const detailScreenForEntity = useMemo(() => {
    if (!activeScreen) return undefined;

    return screens.find(
      (screen: any) => screen.type === "detail" && screen.entity === (activeScreen as any).entity,
    ) as any | undefined;
  }, [activeScreen, screens]);

  const selectedId = activeScreen ? selectedIds[activeScreen.id] ?? null : null;

  const handleRowClick = useCallback(
    (id: string | number | null) => {
      if (!activeScreen) return;
      setSelectedIds((current) => ({ ...current, [activeScreen.id]: id }));
    },
    [activeScreen],
  );

  const handleProjectRowClick = useCallback(
    (row: any) => {
      const projectId = (row as any)?.id;
      if (projectId != null) {
        navigate(`/projects/${projectId}`);
      }
    },
    [navigate],
  );

  const handleProjectEditClick = useCallback(
    (row: any) => {
      const projectId = (row as any)?.id;
      if (projectId == null) return;
      const customerId =
        (row as any)?.customerId ??
        (typeof (row as any)?.customer === "object" ? (row as any)?.customer?.id : null);
      if (customerId != null) {
        handleActiveCustomerChange(String(customerId));
      }
      handleRowClick(projectId);
    },
    [handleActiveCustomerChange, handleRowClick],
  );

  const handleSaved = useCallback(() => {
    setListRefreshKey((previous) => previous + 1);
    setFormResetKey((previous) => previous + 1);
  }, []);

  const handleGenerateRequirement = useCallback(
    async (requirementId: string | number, refetch: () => void) => {
      setGenerationActiveId(requirementId);
      try {
        await triggerRequirementGeneration(requirementId);
        refetch();
        alert("AI generation triggered successfully.");
      } catch (err) {
        alert(err instanceof Error ? err.message : "Failed to trigger AI generation");
      } finally {
        setGenerationActiveId(null);
      }
    },
    [triggerRequirementGeneration],
  );

  const renderGenerateActions = useCallback(
    (row: any, { refetch }: { refetch: () => void }) => (
      <div className="flex flex-col gap-1 items-end">
        <button
          type="button"
          className="button button-primary button-sm"
          onClick={(event: any) => {
            event.stopPropagation();
            void handleGenerateRequirement((row as any)?.id, refetch);
          }}
          disabled={isGeneratingRequirement || generationActiveId === (row as any)?.id}
        >
          {isGeneratingRequirement && generationActiveId === (row as any)?.id ? "Generating…" : "Generate with AI"}
        </button>
        {generationError ? (
          <span className="text-xs text-red-600">
            {generationError instanceof Error ? generationError.message : "Generation failed"}
          </span>
        ) : null}
      </div>
    ),
    [generationActiveId, generationError, handleGenerateRequirement, isGeneratingRequirement],
  );

  const content = useMemo(() => {
    if (isProjectDetailRoute) {
      return <ProjectDetailScreen />;
    }

    if (location.pathname === "/home" || location.pathname === "/longwalls") {
      return <HomeDashboard />;
    }

    if (location.pathname === "/input_data") {
      return (
        <InputDataScreen
          activeCustomerId={activeCustomerId}
          onActiveCustomerChange={handleActiveCustomerChange}
        />
      );
    }

    if (
      location.pathname === "/governance" ||
      location.pathname === "/workspace_settings" ||
      location.pathname.startsWith("/settings/")
    ) {
      return <GovernanceScreen />;
    }

    if (location.pathname === "/portfolio_runs") {
      return <PortfolioRunsScreen />;
    }

    if (location.pathname === "/portfolio") {
      return <PortfolioScreen activeCustomerId={activeCustomerId} />;
    }

    if (location.pathname === "/jarvis") {
      return <JarvisLandingScreen />;
    }

    if (location.pathname === "/runtime-cockpit") {
      return <RuntimeCockpitScreen />;
    }

    if (location.pathname === "/milestones") {
      return <LongWallsMilestonesScreen />;
    }

    if (location.pathname === "/forum") {
      return <ForumScreen />;
    }

    if ((location.pathname === "/foundry" || location.pathname === "/foundry-v2") && FEATURE_JARVIS_V2) {
      return <FoundryV2Screen />;
    }

    if (FEATURE_JARVIS_V2 && location.pathname === "/governance-v2") {
      return <GovernanceV2Screen />;
    }

    if (FEATURE_JARVIS_V2 && location.pathname === "/jarvis-v2") {
      return <JarvisV2WorkspaceScreen />;
    }

    if (
      location.pathname === "/atlas" ||
      location.pathname === "/ai_roadmap" ||
      location.pathname.startsWith("/ai_roadmap/")
    ) {
      return <AiRoadmapScreen />;
    }

    if (activeScreen?.id === "checklist_items_list") {
      return <DiscoveryReadinessPanel />;
    }

    if (!activeScreen || !baseEntity) {
      return <div className="p-4">No screen configuration available.</div>;
    }

    if (activeScreen.type !== "list") {
      return <div className="p-4">Screen type not supported: {activeScreen.type}</div>;
    }

    const detailEntity = detailScreenForEntity
      ? mergeDetailConfig(baseEntity, detailScreenForEntity as any)
      : baseEntity;

    const listEntity = mergeListConfig(
      baseEntity.list as UiEntityListConfig,
      activeScreen && (activeScreen as any).type === "list" ? (activeScreen as any) : undefined,
    );

    const isCustomersList = activeScreen.id === "customers_list";
    const isProjectsList = activeScreen.id === "projects_list";
    const isRequirementsList = activeScreen.id === "requirements_list";
    const isRequirementsToReviewList = activeScreen.id === "requirements_to_review_list";
    const isRequirementsToGenerateList = activeScreen.id === "requirements_to_generate_list";
    const routeRequirementsView: RequirementsView | null = isRequirementsToReviewList
      ? "to_review"
      : isRequirementsToGenerateList
        ? "to_generate"
        : null;
    const shouldShowRequirementTabs =
      FEATURE_NAV_V01 && (isRequirementsList || isRequirementsToReviewList || isRequirementsToGenerateList);
    const effectiveRequirementsView: RequirementsView =
      (shouldShowRequirementTabs && (routeRequirementsView ?? requirementsViewFromParams ?? "all")) || "all";
    const requirementsListViewProps = shouldShowRequirementTabs && isRequirementsList
      ? {
          enableViewTabs: true,
          defaultView: routeRequirementsView ?? requirementsViewFromParams ?? "all",
          view: effectiveRequirementsView,
          onViewChange: handleRequirementsViewChange,
          renderActionsByView: { to_generate: renderGenerateActions },
        }
      : {};
    const requirementsRouteViewProps =
      shouldShowRequirementTabs && !isRequirementsList && (isRequirementsToReviewList || isRequirementsToGenerateList)
        ? {
            enableViewTabs: true,
            defaultView: routeRequirementsView ?? requirementsViewFromParams ?? "all",
            view: effectiveRequirementsView,
            onViewChange: handleRequirementsViewChange,
          }
        : {};

    return (
      <div className="content-stack">
        {isCustomersList ? (
          <CustomersListScreen
            key={`${listRefreshKey}-${activeCustomerId}`}
            entityName={baseEntity.entityName}
            activeCustomerId={activeCustomerId}
            onActiveCustomerChange={handleActiveCustomerChange}
            onRowClick={(row: any) => handleRowClick((row as any)?.id ?? null)}
          />
        ) : isProjectsList ? (
          <ProjectsListScreen
            key={`${listRefreshKey}-${activeCustomerId}`}
            entityName={baseEntity.entityName}
            filters={(activeScreen as any)?.defaultFilters}
            activeCustomerId={activeCustomerId}
            onActiveCustomerChange={handleActiveCustomerChange}
            onRowClick={handleProjectRowClick}
            onEditClick={handleProjectEditClick}
          />
        ) : isRequirementsList ? (
          <RequirementsListScreen
            key={`${listRefreshKey}-${activeCustomerId}`}
            entityName={baseEntity.entityName}
            filters={(activeScreen as any)?.defaultFilters}
            activeCustomerId={activeCustomerId}
            onActiveCustomerChange={handleActiveCustomerChange}
            onRowClick={(row: any) => handleRowClick((row as any)?.id ?? null)}
            title={(activeScreen as any)?.title as string | undefined}
            {...(requirementsListViewProps as any)}
          />
        ) : isRequirementsToReviewList ? (
          <RequirementsToReviewListScreen
            key={`${listRefreshKey}-${activeCustomerId}`}
            entityName={baseEntity.entityName}
            activeCustomerId={activeCustomerId}
            onActiveCustomerChange={handleActiveCustomerChange}
            onRowClick={(row: any) => handleRowClick((row as any)?.id ?? null)}
            title={(activeScreen as any)?.title as string | undefined}
            {...(requirementsRouteViewProps as any)}
          />
        ) : isRequirementsToGenerateList ? (
          <RequirementsAwaitingGenerationListScreen
            key={`${listRefreshKey}-${activeCustomerId}`}
            entityName={baseEntity.entityName}
            activeCustomerId={activeCustomerId}
            onActiveCustomerChange={handleActiveCustomerChange}
            onRowClick={(row: any) => handleRowClick((row as any)?.id ?? null)}
            title={(activeScreen as any)?.title as string | undefined}
            {...(requirementsRouteViewProps as any)}
          />
        ) : (
          <EntityListScreen
            key={listRefreshKey}
            entity={{ ...baseEntity, list: listEntity }}
            filters={(activeScreen as any)?.defaultFilters}
            onRowClick={(row: any) => handleRowClick((row as any)?.id ?? null)}
            titleOverride={(activeScreen as any)?.title as string | undefined}
          />
        )}

        <div className="form-grid">
          {isCustomersList ? (
            <>
              <CustomerFormPanel mode="create" onSaved={handleSaved} />
              {selectedId ? (
                <CustomerFormPanel
                  key={`${activeScreen.id}-edit-${selectedId}-${formResetKey}`}
                  mode="edit"
                  customerId={selectedId}
                  onSaved={handleSaved}
                  onBack={() => handleRowClick(null)}
                />
              ) : null}
            </>
          ) : isProjectEntity ? (
            <>
              <ProjectFormPanel
                key={`${activeScreen.id}-create-${formResetKey}-${activeCustomerId}`}
                mode="create"
                activeCustomerId={activeCustomerId}
                onSaved={handleSaved}
              />
              {selectedId ? (
                <ProjectFormPanel
                  key={`${activeScreen.id}-edit-${selectedId}-${formResetKey}`}
                  mode="edit"
                  projectId={selectedId}
                  activeCustomerId={activeCustomerId}
                  onSaved={handleSaved}
                  onBack={() => handleRowClick(null)}
                />
              ) : null}
            </>
          ) : (
            <>
              <EntityCreateScreen
                key={`${activeScreen.id}-create-${formResetKey}-${activeCustomerId}`}
                entitySpec={baseEntity}
                initialValues={createInitialValues}
                onSaved={handleSaved}
              />
              {selectedId ? (
                isRequirementEntity ? (
                  <RequirementEditPanel
                    key={`${activeScreen.id}-edit-${selectedId}-${formResetKey}`}
                    entitySpec={baseEntity}
                    id={selectedId}
                    onSaved={handleSaved}
                    onBack={() => handleRowClick(null)}
                    onUpdated={handleSaved}
                  />
                ) : (
                  <EntityEditScreen
                    key={`${activeScreen.id}-edit-${selectedId}-${formResetKey}`}
                    entitySpec={baseEntity}
                    id={selectedId}
                    onSaved={handleSaved}
                    onBack={() => handleRowClick(null)}
                  />
                )
              ) : null}
            </>
          )}
        </div>

        {selectedId && detailEntity.detail ? (
          isProjectEntity ? (
            <ProjectDetailTabs
              key={`${activeScreen.id}-detail-${selectedId}-${formResetKey}`}
              entity={detailEntity}
              id={selectedId}
              title={(detailScreenForEntity as any)?.title ?? detailEntity.label}
              onBack={() => handleRowClick(null)}
              onDeleted={handleSaved}
              specRefreshKey={specRefreshKey}
              onSpecUpdated={() => setSpecRefreshKey((current) => current + 1)}
            />
          ) : isRequirementEntity ? (
            <RequirementDetailScreen
              key={`${activeScreen.id}-detail-${selectedId}-${formResetKey}`}
              entity={detailEntity}
              id={selectedId}
              titleOverride={(detailScreenForEntity as any)?.title ?? detailEntity.label}
              onBack={() => handleRowClick(null)}
              onDeleted={handleSaved}
            />
          ) : (
            <EntityDetailScreen
              key={`${activeScreen.id}-detail-${selectedId}-${formResetKey}`}
              entity={detailEntity}
              id={selectedId}
              titleOverride={(detailScreenForEntity as any)?.title ?? detailEntity.label}
              onBack={() => handleRowClick(null)}
              onDeleted={handleSaved}
            />
          )
        ) : null}
      </div>
    );
  }, [
    activeScreen,
    activeCustomerId,
    baseEntity,
    createInitialValues,
    detailScreenForEntity,
    detailScreenForEntity?.title,
    handleActiveCustomerChange,
    handleRowClick,
    handleProjectRowClick,
    handleRequirementsViewChange,
    handleSaved,
    location.pathname,
    renderGenerateActions,
    requirementsViewFromParams,
    isProjectDetailRoute,
    isProjectEntity,
    isRequirementEntity,
    listRefreshKey,
    selectedId,
    specRefreshKey,
  ]);

  return (
    <>
      <JarvisThemeLink enabled={jarvisThemeEnabled} />
      <div className="studio-shell min-h-screen text-foreground">
        <header
          ref={headerRef}
          className="studio-shell__header fixed inset-x-0 top-0 z-50 border-b border-border/80"
        >
          <div className="studio-shell__header-inner">
            <div className="studio-shell__header-brand">
              <button
                type="button"
                className="button button-secondary button-sm studio-shell__menu-toggle"
                onClick={() => setIsMobileNavOpen((value) => !value)}
                aria-label={isMobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={isMobileNavOpen}
                aria-controls="studio-sidebar-nav"
                ref={menuButtonRef}
              >
                {isMobileNavOpen ? "Menu" : "Menu"}
              </button>
              <div className="studio-shell__brand-panel">
                <img
                  src="/Pericles-Logo.png"
                  alt="Pericles logo"
                  className="studio-shell__logo"
                  onError={(event) => {
                    const image = event.currentTarget;
                    if (image.dataset.logoFallbackApplied === "true") {
                      image.onerror = null;
                      return;
                    }
                    image.dataset.logoFallbackApplied = "true";
                    image.src = "/pericles-logo.svg";
                  }}
                />
              </div>
            </div>
            <div className="studio-shell__header-controls">
              <div className="studio-shell__header-topline">
                <div className="studio-shell__header-title-group">
                  <span className="studio-shell__eyebrow">Workspace cockpit</span>
                  <div className="studio-shell__wordmark">LONGWALLS</div>
                </div>
                <div className="studio-shell__header-summary">
                  <span className="studio-shell__summary-label">Current area</span>
                  <strong className="studio-shell__summary-value">
                    {location.pathname === DEFAULT_HOME_ROUTE || location.pathname === "/home"
                      ? "LongWalls"
                      : activeNavItem?.label ?? "LongWalls"}
                  </strong>
                </div>
              </div>
              <div className="studio-shell__header-selects">
                <div className="studio-shell__header-tenant">
                  <TenantSwitcher showLabel={false} selectClassName="studio-shell__tenant-select" />
                  <SelectInput
                    className="studio-shell__locale-select"
                    value={locale}
                    onChange={(event) => setLocale(event.target.value as "en" | "fr")}
                  >
                    <option value="en">EN</option>
                    <option value="fr">FR</option>
                  </SelectInput>
                </div>
                <div className="studio-shell__header-customer">
                  <span className="studio-shell__select-label">Customer</span>
                  <SelectInput
                    className="studio-shell__customer-select"
                    value={activeCustomerId}
                    onChange={handleCustomerSelect}
                    disabled={isLoadingCustomers}
                  >
                    {customerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              </div>
            </div>
            <div className="studio-shell__header-actions">
              <button
                type="button"
                className="button button-primary button-sm studio-shell__action-button"
                onClick={handleLogout}
              >
                Logout
              </button>
              <button
                type="button"
                className="button button-secondary button-sm studio-shell__action-button"
                onClick={() => setIsDarkMode((value) => !value)}
              >
                {isDarkMode ? "Light mode" : "Dark mode"}
              </button>
            </div>
          </div>
        </header>
        <button
          type="button"
          className={`studio-shell__sidebar-backdrop ${isMobileNavOpen ? "studio-shell__sidebar-backdrop--open" : ""}`}
          aria-label="Close navigation"
          aria-hidden={!isMobileNavOpen}
          tabIndex={isMobileNavOpen ? 0 : -1}
          onClick={() => setIsMobileNavOpen(false)}
        />
        <div className="studio-shell__content-wrap app-shell__content flex w-full flex-col gap-6 lg:flex-row">
          <aside
            ref={sidebarRef}
            className={`studio-shell__sidebar ${isMobileNavOpen ? "studio-shell__sidebar--open" : ""}`}
            id="studio-sidebar-nav"
            role="navigation"
            aria-label="Main navigation"
          >
            <div className="studio-shell__sidebar-card rounded-lg border border-border bg-card p-4 shadow-soft">
              <button
                type="button"
                className="button button-ghost button-sm studio-shell__sidebar-close"
                onClick={() => setIsMobileNavOpen(false)}
                aria-label="Close navigation menu"
              >
                Close
              </button>
              <div className="studio-shell__sidebar-mobile-tools">
                <div className="studio-shell__sidebar-mobile-section">
                  <span className="studio-shell__select-label">Current area</span>
                  <div className="studio-shell__sidebar-current">
                    {location.pathname === DEFAULT_HOME_ROUTE || location.pathname === "/home"
                      ? "LongWalls"
                      : activeNavItem?.label ?? "LongWalls"}
                  </div>
                </div>
                <div className="studio-shell__sidebar-mobile-section">
                  <span className="studio-shell__select-label">Workspace</span>
                  <div className="studio-shell__sidebar-mobile-tenant">
                    <TenantSwitcher
                      showLabel={false}
                      selectClassName="studio-shell__tenant-select studio-shell__tenant-select--mobile"
                    />
                    <SelectInput
                      className="studio-shell__locale-select studio-shell__locale-select--mobile"
                      value={locale}
                      onChange={(event) => setLocale(event.target.value as "en" | "fr")}
                    >
                      <option value="en">EN</option>
                      <option value="fr">FR</option>
                    </SelectInput>
                  </div>
                </div>
                <div className="studio-shell__sidebar-mobile-section">
                  <span className="studio-shell__select-label">Customer</span>
                  <SelectInput
                    className="studio-shell__customer-select"
                    value={activeCustomerId}
                    onChange={handleCustomerSelect}
                    disabled={isLoadingCustomers}
                  >
                    {customerOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div className="studio-shell__sidebar-mobile-actions">
                  <button
                    type="button"
                    className="button button-primary button-sm studio-shell__action-button"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                  <button
                    type="button"
                    className="button button-secondary button-sm studio-shell__action-button"
                    onClick={() => setIsDarkMode((value) => !value)}
                  >
                    {isDarkMode ? "Light mode" : "Dark mode"}
                  </button>
                </div>
              </div>
              <SidebarNav
                className="gap-1"
                items={sidebarItems}
                activeHref={activeHref}
                onNavigate={handleNavigate}
              />
            </div>
          </aside>
          <main className="studio-shell__main flex-1 min-w-0 px-6">
            {shouldShowFoundryV2Banner && foundryV2Navigation ? (
              <div
                className={`mb-4 rounded-xl border px-4 py-3 shadow-sm ${rolloutBannerClass(foundryV2Navigation.shellBanner.tone)}`}
                data-testid="foundry-v2-rollout-banner"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em]">Cutover posture</div>
                    <div className="text-base font-semibold">{foundryV2Navigation.shellBanner.headline}</div>
                    <p className="max-w-3xl text-sm opacity-90">{foundryV2Navigation.shellBanner.detail}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-right text-xs opacity-80">
                    <div>Mode: {foundryV2Navigation.rolloutMode.toUpperCase()}</div>
                    <div>Active path: {foundryV2Navigation.activePath}</div>
                    <div>Legacy fallback: {foundryV2Navigation.legacyFallbackEnabled ? "yes" : "no"}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={() => navigate(foundryV2Navigation.shellBanner.ctaHref)}
                  >
                    {foundryV2Navigation.shellBanner.ctaLabel}
                  </button>
                  <span className="text-xs opacity-80">{foundryV2Navigation.moduleEntry.reason}</span>
                </div>
                {foundryV2NavigationError ? (
                  <div className="mt-2 text-xs text-red-700">{foundryV2NavigationError}</div>
                ) : null}
              </div>
            ) : null}
            <div className="studio-shell__panel studio-shell__panel--animated">{content}</div>
          </main>
        </div>
      </div>
    </>
  );
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading, activeTenantId, tenants } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Loading session…</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!activeTenantId && tenants.length > 1) {
    return <TenantSelectScreen />;
  }

  return children;
}

export function AppShell(): JSX.Element {
  return (
    <AuthProvider>
      <AuthenticatedPericlesProvider>
        <InternalCapabilitiesProvider>
          <I18nProvider>
            <BrowserRouter>
              <div className="app-shell">
                <Routes>
                  <Route path="/login" element={<LoginScreen />} />
                  <Route
                    path="/internal/admin"
                    element={
                      <RequireAuth>
                        <Navigate to="/workspace_settings#internal-admin" replace />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <RequireAuth>
                        <Navigate to={DEFAULT_HOME_ROUTE} replace />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/projects/:projectId"
                    element={
                      <RequireAuth>
                        <AppShellContent />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/*"
                    element={
                      <RequireAuth>
                        <AppShellContent />
                      </RequireAuth>
                    }
                  />
                </Routes>
              </div>
            </BrowserRouter>
          </I18nProvider>
        </InternalCapabilitiesProvider>
      </AuthenticatedPericlesProvider>
    </AuthProvider>
  );
}
